import type { TeacherCohortRecord } from "../auth/api-client";

export type CohortListSortKey = "label" | "course" | "created" | "updated";

export const COHORT_LIST_SORT_OPTIONS: { readonly key: CohortListSortKey; readonly label: string }[] = [
  { key: "label", label: "Name (A-Z)" },
  { key: "course", label: "Course (A-Z)" },
  { key: "created", label: "Recently created" },
  { key: "updated", label: "Recently updated" },
];

const SORT_STORAGE_KEY = "ccs-teacher-cohorts-sort";

function isSortKey(raw: string | null): raw is CohortListSortKey {
  return COHORT_LIST_SORT_OPTIONS.some((o) => o.key === raw);
}

export function loadCohortListSortKey(): CohortListSortKey {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (isSortKey(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "label";
}

export function saveCohortListSortKey(key: CohortListSortKey): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, key);
  } catch {
    /* ignore */
  }
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function compareNewest(a: string, b: string): number {
  return Date.parse(b) - Date.parse(a);
}

export function sortTeacherCohorts(
  cohorts: readonly TeacherCohortRecord[],
  key: CohortListSortKey
): TeacherCohortRecord[] {
  const copy = [...cohorts];
  copy.sort((a, b) => {
    switch (key) {
      case "label":
        return compareText(a.label, b.label) || compareText(a.courseTitle, b.courseTitle) || a.id.localeCompare(b.id);
      case "course":
        return compareText(a.courseTitle, b.courseTitle) || compareText(a.label, b.label) || a.id.localeCompare(b.id);
      case "created":
        return compareNewest(a.createdAt, b.createdAt) || b.id.localeCompare(a.id);
      case "updated":
        return compareNewest(a.updatedAt, b.updatedAt) || b.id.localeCompare(a.id);
      default:
        return 0;
    }
  });
  return copy;
}
